import { DateTime } from 'luxon'

function getDateTime(value: string | number | Date) {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value)
  } else if (typeof value === 'string') {
    return DateTime.fromISO(value)
  } else {
    return DateTime.fromMillis(value)
  }
}

/**
 * Converts a value into a nice date format
 * @param {string | number | Date} value value to be converted, accepts any valid `Date` format
 */
export function prettyDate(value: string | number | Date) {
  if (!value) { return '' }

  return getDateTime(value).toFormat('dd MMM yyyy')
}

export function shortDate(value: string | number | Date) {
  if (!value) { return '' }
  return getDateTime(value).toFormat('dd\'/\'MM\'/\'yyyy')
}

/**
 * Converts a value into a nice time format
 * @param {string | number | Date} value value to be converted, accepts any valid `Date` format
 */
export function prettyTime(value: string | number | Date) {
  if (!value) { return '' }
  return getDateTime(value).toFormat('hh\':\'mm a')
}

/**
 * Converts a value into a nice date-time format
 * @param {string | number |Date} [value] value to be converted, accepts anyvalid `Date` format
 */
export function prettyDateTime(value?: string | number | Date) {
  return value ? `${prettyDate(value)} - ${prettyTime(value)}` : '-'
}

/**
 * Converts text into title case, removing hyphens and replaces them with spaces
 * @param {string} text Text to convert to title case
 */
export function toTitleCase(text: string) {
  if (!text) {
    return text
  }
  return text.replace(/\w*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()).replace('-', ' ')
}

/**
 * Converts a date to a string formt `YYYY-MM-dd`
 * @param {Date} date Date to be converted to a string
 */
export function convertToLocalDateString(date: Date) {
  if (!date) { return null }
  const month = date.getMonth() + 1
  const paddedMonth = ('0' + month).substring(month > 9 ? 1 : 0)
  date = new Date(date)
  return [date.getFullYear(), paddedMonth, date.getDate()].join('-')
}

/**
 * Displays a value in standard dollar format
 *
 * @param {number} value The number value to display as a dollar value
 * @returns {string} The value as a dollar string (prefixed '$' and 2 decimal places)
 */
export function dollarDisplay(value: number) {
  if (value === 0) {
    return '$0.00'
  }
  return value ? `$${Number.parseFloat(`${value}`).toFixed(2)}` : '$ -'
}

export function centDisplay(value: number) {
  return dollarDisplay(value / 100)
}

/**
 * Returns the greatest common divisor of two numbers
 * @param {number} a First number
 * @param {number} b Second number
 */
export function greatestCommonDivisor(a: number, b: number): number {
  if (!b) {
    return b === 0 ? a : NaN
  }
  return greatestCommonDivisor(b, a % b)
}

/**
 * Returns the least common multiple of two numbers
 * @param {number} a First number
 * @param {number} b Second number
 */
export function leastCommonMultiple(a: number, b: number): number {
  return a * b / greatestCommonDivisor(a, b)
}

/**
 * Returns the least common multiple of an array of numbers
 * @param {number[]} array
 */
export function leastCommonMultipleArray(array: number[]) {
  let n = 1
  for (let i = 0; i < array.length; ++i) {
    n = leastCommonMultiple(array[i], n)
  }
  return n
}

/**
 * Sorts an array of objects based on a property (string or number property).
 * It sorts the array alphabetically or numerically.
 * Prefix any name with `-` to sort it in descending order.
 * @param {string} property
 */
export function dynamicSort(property: string) {
  let sortOrder = 1
  if (property[0] === '-') {
    sortOrder = -1
    property = property.substr(1)
  }
  return function(a: any, b: any) {
    const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0
    return result * sortOrder
  }
}

export function mobileCheck() {
  let check = false;
  (function(a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) { check = true } })(navigator.userAgent || navigator.vendor || (window as any)['opera']) // eslint-disable-line
  return check
}

export function downloadFile(url: string) {
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('target', '_blank')
  link.click()
  link.remove()
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) {
    return bytes + ' B'
  } else if (bytes < 1048576) {
    return (bytes / 1024).toFixed(0) + ' KB'
  } else if (bytes < 1073741824) {
    return (bytes / 1048576).toFixed(2) + ' MB'
  } else {
    return (bytes / 1073741824).toFixed(2) + ' GB'
  }
}

export function sameValues<T>(a: T, b: T) {
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)] as (keyof T)[]))
  return keys.every(key => a[key] === b[key])
}
